"use client";

import { useState, useEffect, useRef, useCallback, useId } from "react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { Search, Car, MapPin, Tag, ArrowRight } from "lucide-react";
import { createClient } from "@/lib/supabase/client";
import { carDetailPath } from "@/lib/car-slug";

interface Suggestion {
  type: "brand" | "model" | "car";
  label: string;
  sublabel?: string;
  href: string;
}

function highlight(text: string, query: string) {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-transparent text-[#E63946] font-semibold not-italic">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
}

export interface CatalogSearchBarProps {
  compact?: boolean;
  elevatedDropdown?: boolean;
  /** Z-index for suggestions when rendered inside a modal overlay */
  modalLayer?: boolean;
  autoFocus?: boolean;
  className?: string;
  onNavigate?: () => void;
}

export default function CatalogSearchBar({
  compact = false,
  elevatedDropdown = false,
  modalLayer = false,
  autoFocus = false,
  className = "",
  onNavigate,
}: CatalogSearchBarProps) {
  const t = useTranslations("Hero");
  const router = useRouter();
  const listId = useId();
  const supabase = createClient();
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchSuggestions = useCallback(
    async (q: string) => {
      if (q.trim().length < 1) {
        setSuggestions([]);
        return;
      }

      const { data } = await supabase
        .from("cars")
        .select("id, slug, title, brand, model, year, location")
        .or(`brand.ilike.%${q}%,model.ilike.%${q}%,title.ilike.%${q}%`)
        .eq("is_active", true)
        .limit(20);

      if (!data) return;

      const brands = [...new Set(data.map((c) => c.brand).filter(Boolean))]
        .filter((b) => b.toLowerCase().includes(q.toLowerCase()))
        .slice(0, 3)
        .map((b): Suggestion => ({ type: "brand", label: b, href: `/cars?brand=${encodeURIComponent(b)}` }));

      const modelSeen = new Set<string>();
      const models: Suggestion[] = [];
      for (const c of data) {
        if (!c.model || !c.brand) continue;
        const slug = `${c.brand}\u0000${c.model}`;
        if (modelSeen.has(slug)) continue;
        if (!`${c.brand} ${c.model}`.toLowerCase().includes(q.toLowerCase())) continue;
        modelSeen.add(slug);
        models.push({
          type: "model",
          label: `${c.brand} ${c.model}`,
          sublabel: c.brand,
          href: `/cars?brand=${encodeURIComponent(c.brand)}&model=${encodeURIComponent(c.model)}`,
        });
        if (models.length >= 3) break;
      }

      const cars = data
        .filter((c) => c.title?.toLowerCase().includes(q.toLowerCase()))
        .slice(0, 4)
        .map(
          (c): Suggestion => ({
            type: "car",
            label: c.title,
            sublabel: c.location ?? undefined,
            href: carDetailPath(c),
          })
        );

      setSuggestions([...brands, ...models, ...cars].slice(0, 8));
      setOpen(true);
      setActiveIdx(-1);
    },
    [supabase]
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(() => fetchSuggestions(query), 220);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, fetchSuggestions]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setOpen(false);
    const q = query.trim();
    if (!q) {
      router.push("/cars");
      onNavigate?.();
      return;
    }
    router.push(`/cars/search?q=${encodeURIComponent(q)}`);
    onNavigate?.();
  }

  function pickSuggestion(s: Suggestion) {
    setQuery(s.label);
    setOpen(false);
    router.push(s.href);
    onNavigate?.();
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (!open || !suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, -1));
    }
    if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      pickSuggestion(suggestions[activeIdx]);
    }
    if (e.key === "Escape") setOpen(false);
  }

  const typeIcon = (type: Suggestion["type"]) => {
    if (type === "brand") return <Tag size={12} className="text-purple-400" />;
    if (type === "model") return <Car size={12} className="text-blue-400" />;
    return <Car size={12} className="text-gray-400" />;
  };

  const dz = modalLayer ? "z-[260]" : elevatedDropdown ? "z-[100]" : "z-50";
  const hasList = open && suggestions.length > 0;

  const outerRound = compact
    ? hasList
      ? "rounded-lg rounded-b-none"
      : "rounded-lg"
    : hasList
      ? "rounded-2xl rounded-b-none shadow-md ring-1 ring-[#E63946]/20"
      : "rounded-2xl shadow-sm ring-1 ring-black/5 dark:ring-white/10";

  const innerPad = compact ? "gap-1.5 py-1 pl-2 pr-0.5" : "gap-3 py-3 pl-5 pr-2";
  const searchIconSz = compact ? 14 : 18;
  const inputText = compact ? "text-xs leading-tight" : "text-base";

  const submitCls = compact
    ? "flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-400 transition hover:bg-gray-100 hover:text-[#E63946] dark:hover:bg-gray-800 dark:hover:text-[#ff6b7a]"
    : "flex min-h-10 shrink-0 items-center justify-center rounded-xl px-6 text-sm font-semibold tracking-tight text-white shadow-sm transition hover:bg-[#c1121f] active:scale-[0.98] sm:min-h-11 bg-[#E63946]";

  const shellCls = compact
    ? hasList
      ? "border border-b-0 border-gray-300/90 dark:border-gray-600"
      : "border border-gray-200/90 dark:border-gray-700/90 focus-within:border-gray-300 focus-within:shadow-sm dark:focus-within:border-gray-600"
    : hasList
      ? "border border-b-0 border-[#E63946]/50 ring-2 ring-[#E63946]/20 dark:border-[#ff6b7a]/40 dark:ring-[#ff6b7a]/25"
      : "border border-gray-200/90 focus-within:border-[#E63946]/40 focus-within:shadow-[0_0_0_3px_rgba(230,57,70,0.12)] dark:border-gray-700/90 dark:focus-within:border-[#ff6b7a]/40 dark:focus-within:shadow-[0_0_0_3px_rgba(255,107,122,0.15)]";

  return (
    <div className={`w-full ${className}`}>
      <form onSubmit={handleSearch} noValidate>
        <div ref={wrapperRef} className="relative">
          {/* z-10: строка ввода выше выпадающего списка, если браузер даёт подпиксельное наложение */}
          <div
            className={`relative z-10 flex min-h-0 w-full items-stretch overflow-visible bg-white transition-[border-color,box-shadow] dark:bg-gray-950 ${outerRound} ${shellCls}`}
          >
            <label
              htmlFor={`catalog-search-${listId}`}
              className={`flex flex-1 min-w-0 items-center ${compact ? "min-h-8 sm:min-h-9" : "min-h-10 sm:min-h-11"} ${innerPad}`}
            >
              <Search
                size={searchIconSz}
                strokeWidth={2}
                className="flex-shrink-0 text-gray-400 opacity-75 dark:text-gray-500"
                aria-hidden
              />
              <input
                id={`catalog-search-${listId}`}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setOpen(true);
                }}
                onFocus={() => {
                  if (suggestions.length) setOpen(true);
                }}
                onKeyDown={onKeyDown}
                placeholder={compact ? t("placeholderNav") : t("placeholder")}
                className={`min-w-0 flex-1 border-0 bg-transparent outline-none caret-[#E63946] placeholder:text-gray-400 dark:placeholder:text-gray-500 ${compact ? "min-h-0 py-0 font-normal" : "min-h-10 py-2 font-semibold tabular-nums placeholder:font-normal sm:min-h-11"} ${inputText} text-gray-900 dark:text-gray-50`}
                autoComplete="off"
                autoFocus={autoFocus}
                aria-expanded={hasList}
                aria-controls={hasList ? listId : undefined}
                aria-haspopup="listbox"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => {
                    setQuery("");
                    setSuggestions([]);
                    setOpen(false);
                  }}
                  aria-label={t("clearAria")}
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-lg leading-none text-gray-300 outline-none hover:bg-gray-100 hover:text-gray-500 dark:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-400"
                >
                  ×
                </button>
              )}
            </label>

            <div className="flex shrink-0 items-center pr-1">
              <button type="submit" className={submitCls} aria-label={t("find")}>
                {compact ? (
                  <ArrowRight size={15} aria-hidden strokeWidth={2} />
                ) : (
                  <span>{t("find")}</span>
                )}
              </button>
            </div>
          </div>

          {hasList && (
            <div
              id={listId}
              role="listbox"
              className={`absolute left-0 right-0 overflow-y-auto overflow-x-hidden bg-white dark:bg-gray-900 ${dz} ${
                compact
                  ? "top-full max-h-[min(62vh,16rem)] rounded-b-lg border border-t-0 border-gray-300/90 shadow-md sm:max-h-[min(70vh,18rem)] dark:border-gray-600"
                  : modalLayer
                    ? "top-full max-h-[min(65vh,26rem)] rounded-b-2xl border-x border-b border-[#E63946]/50 shadow-xl sm:max-h-[min(72vh,30rem)] dark:border-[#ff6b7a]/40"
                    : "top-full max-h-[min(70vh,22rem)] rounded-b-2xl border-x border-b border-[#E63946]/50 shadow-xl dark:border-[#ff6b7a]/40"
              }`}
            >
              {suggestions.map((s, i) => (
                <button
                  key={`${s.type}-${s.label}-${i}`}
                  type="button"
                  role="option"
                  aria-selected={i === activeIdx}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pickSuggestion(s);
                  }}
                  onMouseEnter={() => setActiveIdx(i)}
                  className={`flex w-full items-center gap-2 text-left transition-colors first:pt-2 last:pb-2 sm:gap-3 ${
                    compact ? "px-2.5 py-1.5 sm:px-3 sm:py-2" : "gap-3 px-4 py-2.5 first:pt-3 last:pb-3"
                  } ${i === activeIdx ? "bg-red-50/90 dark:bg-red-950/30" : "hover:bg-gray-50 dark:hover:bg-gray-800"} ${i < suggestions.length - 1 ? "border-b border-gray-100 dark:border-gray-800" : ""}`}
                >
                  <span className={`flex flex-shrink-0 items-center justify-center rounded bg-gray-100 dark:bg-gray-800 ${compact ? "h-5 w-5" : "h-6 w-6 rounded-md"}`}>
                    {typeIcon(s.type)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className={`truncate font-medium text-gray-800 dark:text-gray-100 ${compact ? "text-xs" : "text-sm"}`}>{highlight(s.label, query)}</p>
                    {s.sublabel && (
                      <p className="flex items-center gap-1 truncate text-[11px] text-gray-400 dark:text-gray-500 sm:text-xs">
                        {s.type === "car" && <MapPin size={9} className="shrink-0" />}
                        {s.sublabel}
                      </p>
                    )}
                  </div>
                  <span className={`flex-shrink-0 font-medium uppercase tracking-wide text-gray-300 dark:text-gray-600 ${compact ? "text-[9px] sm:text-[10px]" : "text-[10px]"}`}>
                    {s.type === "brand" ? t("suggestionBrand") : s.type === "model" ? t("suggestionModel") : t("suggestionCar")}
                  </span>
                </button>
              ))}
              <div className={`border-t border-gray-100 bg-gray-50/95 dark:border-gray-800 dark:bg-gray-950/90 ${compact ? "px-2 py-1.5" : "sticky bottom-0 px-4 py-3 backdrop-blur-sm"}`}>
                <button
                  type="submit"
                  className={`flex w-full items-center justify-center gap-1.5 rounded-md font-semibold text-[#E63946] transition-colors hover:bg-red-50 dark:hover:bg-red-950/30 ${
                    compact ? "py-1.5 text-xs" : "gap-2 py-2 text-sm"
                  }`}
                >
                  <Search size={compact ? 12 : 14} />
                  {t("findAll", { query })}
                </button>
              </div>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
